<script setup>
  import Layout from "@/layouts/main.vue";
  import PageHeader from "@/components/page-header";
  import '@jobinsjp/vue3-datatable/dist/style.css';
  import { DataTable, TableBody, TableHead, TableBodyCell } from '@jobinsjp/vue3-datatable';
  import Multiselect from '@suadelabs/vue3-multiselect';
  import "@suadelabs/vue3-multiselect/dist/vue3-multiselect.css";
  import { truncate, cloneDeep, isEqual } from 'lodash';
  import Swal from 'sweetalert2';
  import axios from 'axios';
  import { authHeader } from './../../../helpers/authservice/auth-header';
  import { computed, onMounted, ref } from 'vue';
  import { useStore } from 'vuex';
  import { required, helpers, maxLength } from "@vuelidate/validators";
  import useVuelidate from "@vuelidate/core";

  const store = useStore();
  const loading = computed(() => store.state.globals.loading);
  const couriers = computed(() => store.state.courier.couriers);
  const pagination = computed(() => store.state.courier.pagination);
  const lguOptions = computed(() => store ? store.state.lgu.dropdownLGUs : []);
  const title = ref("Couriers");
  const items = ref([
    {
      text: "Home",
      href: "/"
    },
    {
      text: "Delivery",
    },
    {
      text: "Couriers",
      active: true,
    }
  ])

  const file = ref(null);
  const handleFileUpload = () => {
    cloneCourier.value.logo = file.value.files[0];
  }

  const courier = ref({
    id: '',
    name: '',
    logo: '',
    description: '',
    status: true,
    lgus: []
  })

  const cloneCourier = ref({
    id: '',
    name: '',
    logo: '',
    description: '',
    status: true,
    lgus: []
  })

  const isLoading = ref(false);

  const notification = computed(() => store.state.notification);

  const query = ref({
    search: ''        
  })

  const rules = {
      name: {
          required: helpers.withMessage("Courier Name is required", required)
      },
      status: {
          required: helpers.withMessage("Courier Status is required", required)
      },
      lgus: {
        required: helpers.withMessage('Select at least one LGU', required)
      },
      description: {
        maxLength: helpers.withMessage("Description reach its maximum value", maxLength(500))
      }
  };

  const submitted = ref(false);

  const v$ = useVuelidate(rules, courier);

  const getAllCouriers = async (dt) => {
    isLoading.value = true

    await store.dispatch('courier/getCouriers', {
      page: dt.page,
      pageSize: dt.per_page,
      sortColumn: 'updated_at',
      search: dt.search,
      orderBy: 'desc'
    });

    setTimeout(() => {
      isLoading.value = false
    }, 500)
  }

  const getCourierDetails = async (id) => {
    store.dispatch('globals/loading', null, { root: true });

    try {
        const { data } = await axios.get(`/tracking_courier/${id}`, {
            headers: authHeader()
        });
        const _data = data.data
        _data.status = data.data.status === 1 ? true : false
        courier.value = _data;
        cloneCourier.value = cloneDeep(courier.value);
        file.value.value = '';
        store.dispatch('notification/clear', null, { root: true });
        store.dispatch('globals/loaded', null, { root: true });
    } catch (err) {
        console.log(err);
        store.dispatch('globals/loaded', null, { root: true });
    }
}

  const deleteCourier = (id, name) => {
      Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        cancelButtonColor: "#f46a6a",
        confirmButtonText: "Delete",
      }).then(async (result) => {
        if (result.value) {
          await store.dispatch('courier/deleteCourier', { id, name });
        }
      });
  }

  const clearNotification = () => {
    store.dispatch('notification/clear', null, { root: true });
  }

  const updateCourierDetails = async () => {
    try {
      submitted.value = true
      v$.value.$touch();
      if (v$.value.$invalid) return;

      if (!file.value.value) {
        delete cloneCourier.value.logo;
      }

      await store.dispatch('courier/updateCourier', cloneCourier.value);
    } catch (err) {
      console.log(err);
    }
  }

  const getLGUs = (lgus) => {
    if (lgus && lgus.length > 0) {
        const lgusArray = lgus.map(lgu => lgu.name);
        return lgusArray.join(', ');
    }
    return 'N/A';
  }

  onMounted(() => {
    store.dispatch('lgu/getDropdownLGUs')
  })
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <b-overlay :show="loading">
      <div class="d-flex mb-4 justify-content-between">
        <div>
          <h1 class="fs-4 text">Couriers</h1>
          <small>Lists of all available couriers</small>
        </div>
        <div>
          <router-link
              to="/delivery/couriers/create"
              class="btn btn-primary right"
            >
              <i class="ri-add-fill label-icon align-middle fs-16 me-2"></i>
              New Courier
          </router-link>
        </div>
      </div>

      <div class="row">
          <div class="col lg-12">
              <data-table :rows="couriers"
                  :pagination="pagination"
                  striped
                  :loading="loading"
                  :query="query"
                  filter
                  hoverable
                  sortable
                  @loadData="getAllCouriers"
              >
                <template #thead>
                  <table-head>Courier Logo</table-head>
                  <table-head>Courier ID</table-head>
                  <table-head>Courier Name</table-head>
                  <table-head>Courier Status</table-head>
                  <table-head>LGU</table-head>
                  <table-head>Action</table-head>
                </template>

                <template #tbody="{row}">
                    <table-body>
                      <img v-if="row.logo" :src="row.logo" :alt="row.name" width="100" height="60" class="img-responsive"/>
                      <span v-else>N/A</span>
                    </table-body>
                    <table-body v-text="row.id" />
                    <table-body v-text="row.name ? truncate(row.name, { length: 30 }) : 'N/A'" />
                    <table-body>
                      <b-badge :variant="row.status ? 'success' : 'danger'">
                        {{ row.status ? 'Enabled' : 'Disabled' }}
                      </b-badge>
                    </table-body>
                    <table-body v-text="truncate(getLGUs(row.lgus), { length: 25 })" :title="getLGUs(row.lgus)" />
                    <table-body>
                      <button
                          type="button"
                          class="btn btn-secondary mr-2"
                          title="Edit"
                          data-bs-toggle="modal"
                          data-bs-target="#editCourierModal"
                          @click="getCourierDetails(row.id)"
                        >
                        <i class="mdi mdi-pencil label-icon align-middle"></i>
                      </button>
                      <button
                          @click="deleteCourier(row.id, row.name)"
                          class="btn btn-danger"
                          title="Delete"
                      >
                        <i class="mdi mdi-delete"></i>
                      </button>
                    </table-body>
                </template>

                <template #empty>
                  <TableBodyCell colspan="10">
                      No couriers found.
                  </TableBodyCell>
              </template>
            </data-table>
          </div>
      </div>
    </b-overlay>
  </Layout>
   <!-- Varying modal content -->
  <div class="modal fade" role="dialog" id="editCourierModal" tabindex="-1" aria-labelledby="varyingcontentModalLabel" aria-hidden="true">
      <div class="modal-dialog">
          <div class="modal-content">
              <b-overlay :show="loading">
                <div class="modal-header">
                    <h5 class="modal-title" id="varyingcontentModalLabel">Courier Details</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                      <form method="POST" enctype="multipart/form-data">
                          <b-alert v-if="notification && notification.message"
                              show variant="success" class="fade show" role="alert">
                              {{ notification.message }}
                          </b-alert>

                          <div v-if="notification && notification.messages.length > 0">
                              <b-alert v-for="(message, index) in notification.messages" :key="index"
                                  show variant="danger" class="fade show" role="alert">
                                  {{ message }}
                              </b-alert>
                          </div>
                          <div class="mb-3">
                              <label for="logoFile" class="form-label">Logo</label>
                              <input @input="clearNotification" ref="file" @change="handleFileUpload" class="form-control" type="file" id="logoFile">
                          </div>
                          <div class="mb-3">
                              <label for="name" class="form-label required">Name</label>
                              <input 
                                autofocus
                                @input="clearNotification"
                                :class="{ 'is-invalid': submitted && v$.name.$error }"
                                type="text" class="form-control" id="name" v-model="cloneCourier.name">
                              <div
                                  v-for="item of v$.name.$errors"
                                  :key="item.$uid"
                                  class="invalid-feedback"
                              >
                                <span v-if="item.$message">{{ item.$message }}</span>
                              </div>
                          </div>
                          <div class="mb-3">
                              <label for="lgus" class="form-label required">LGU</label>
                              <multiselect
                                  id="lgus"
                                  :class="{ 'is-invalid': submitted && v$.lgus.$error }"
                                  :multiple="true"
                                  v-model="cloneCourier.lgus"
                                  :close-on-select="true"
                                  :options="lguOptions"
                                  placeholder="Select LGU(s)"
                                  label="name"
                                  track-by="name"
                                  @input="clearNotification"
                              />
                               <div
                                  v-for="item of v$.lgus.$errors"
                                  :key="item.$uid"
                                  class="invalid-feedback"
                                >
                                    <span v-if="item.$message">{{ item.$message }}</span>
                                </div>
                          </div>
                          <div class="mb-3">
                              <label for="description" class="form-label">Description</label>
                              <textarea 
                                  :class="{ 'is-invalid': submitted && v$.description.$error }"
                                   @input="clearNotification"
                                  class="form-control" id="descrition" v-model="cloneCourier.description"></textarea>
                              <div
                                  v-for="item of v$.description.$errors"
                                  :key="item.$uid"
                                  class="invalid-feedback"
                              >
                                <span v-if="item.$message">{{ item.$message }}</span>
                              </div>
                          </div>
                          <div class="mb-3">
                              <label for="courier-status" class="form-label">Status</label>
                              <div class="form-check form-switch form-switch-lg" dir="ltr">
                                  <input @input="clearNotification" type="checkbox" name="status" v-model="cloneCourier.status" class="form-check-input" id="courier-status" checked="">
                                  <label class="form-check-label" for="customSwitchsizelg">{{ cloneCourier.status ? 'Enabled' : 'Disabled' }}</label>
                              </div>
                          </div>
                      </form>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-danger" data-bs-dismiss="modal">
                      <i class="mdi mdi-close-thick"></i>
                      Close
                    </button>
                    <button :disabled="isEqual(courier, cloneCourier)" @click.prevent="updateCourierDetails" type="button" class="btn btn-success">
                      <i class="mdi mdi-content-save"></i>
                      Update Courier
                    </button>
                </div>
              </b-overlay>
          </div>
      </div>
    </div>
</template>